import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    serverTimestamp,
    arrayUnion,
    arrayRemove,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const STARTUPS_COLLECTION = 'startups';
const TEAMS_COLLECTION = 'teams';
const COMPETITIONS_COLLECTION = 'competitions';

// ==================== STARTUP OPERATIONS ====================

export const createStartup = async (startupData) => {
    try {
        const docRef = await addDoc(collection(db, STARTUPS_COLLECTION), {
            ...startupData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: 'Active',
            teamMembers: [],
            competitions: [],
        });
        // console.log('✅ Startup created:', docRef.id);
        return { id: docRef.id, ...startupData };
    } catch (error) {
        console.error('❌ Error creating startup:', error);
        throw new Error(`Failed to create startup: ${error.message}`);
    }
};

export const getStartups = async () => {
    try {
        const querySnapshot = await getDocs(collection(db, STARTUPS_COLLECTION));
        const startups = [];
        querySnapshot.forEach((doc) => {
            startups.push({ id: doc.id, ...doc.data() });
        });
        // console.log('✅ Startups fetched:', startups.length);
        return startups;
    } catch (error) {
        console.error('❌ Error fetching startups:', error);
        throw new Error(`Failed to fetch startups: ${error.message}`);
    }
};

export const getStartupById = async (startupId) => {
    try {
        const docRef = doc(db, STARTUPS_COLLECTION, startupId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            throw new Error('Startup not found');
        }

        // console.log('✅ Startup fetched:', startupId);
        return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
        console.error('❌ Error fetching startup:', error);
        throw new Error(`Failed to fetch startup: ${error.message}`);
    }
};

export const updateStartup = async (startupId, updates) => {
    try {
        const docRef = doc(db, STARTUPS_COLLECTION, startupId);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        });
        // console.log('✅ Startup updated:', startupId);
        return { id: startupId, ...updates };
    } catch (error) {
        console.error('❌ Error updating startup:', error);
        throw new Error(`Failed to update startup: ${error.message}`);
    }
};

export const deleteStartup = async (startupId) => {
    try {
        const docRef = doc(db, STARTUPS_COLLECTION, startupId);
        await deleteDoc(docRef);
        // console.log('✅ Startup deleted:', startupId);
    } catch (error) {
        console.error('❌ Error deleting startup:', error);
        throw new Error(`Failed to delete startup: ${error.message}`);
    }
};

export const searchStartups = async (searchTerm) => {
    try {
        const startups = await getStartups();
        return startups.filter(
            (startup) =>
                startup.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                startup.founder?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                startup.category?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    } catch (error) {
        console.error('❌ Error searching startups:', error);
        throw new Error(`Failed to search startups: ${error.message}`);
    }
};

// ==================== TEAM MEMBER OPERATIONS ====================

export const addTeamMember = async (startupId, memberData) => {
    try {
        const docRef = await addDoc(collection(db, TEAMS_COLLECTION), {
            startupId,
            ...memberData,
            createdAt: serverTimestamp(),
        });

        const startupRef = doc(db, STARTUPS_COLLECTION, startupId);
        await updateDoc(startupRef, {
            teamMembers: arrayUnion(docRef.id),
        });

        // console.log('✅ Team member added:', docRef.id);
        return { id: docRef.id, ...memberData };
    } catch (error) {
        console.error('❌ Error adding team member:', error);
        throw new Error(`Failed to add team member: ${error.message}`);
    }
};

export const getTeamMembers = async (startupId) => {
    try {
        const q = query(collection(db, TEAMS_COLLECTION), where('startupId', '==', startupId));
        const querySnapshot = await getDocs(q);
        const members = [];

        querySnapshot.forEach((doc) => {
            members.push({ id: doc.id, ...doc.data() });
        });

        // console.log('✅ Team members fetched:', members.length);
        return members;
    } catch (error) {
        console.error('❌ Error fetching team members:', error);
        throw new Error(`Failed to fetch team members: ${error.message}`);
    }
};

export const deleteTeamMember = async (startupId, memberId) => {
    try {
        const memberRef = doc(db, TEAMS_COLLECTION, memberId);
        await deleteDoc(memberRef);

        const startupRef = doc(db, STARTUPS_COLLECTION, startupId);
        await updateDoc(startupRef, {
            teamMembers: arrayRemove(memberId),
        });

        // console.log('✅ Team member deleted:', memberId);
    } catch (error) {
        console.error('❌ Error deleting team member:', error);
        throw new Error(`Failed to delete team member: ${error.message}`);
    }
};

export const updateTeamMember = async (memberId, updates) => {
    try {
        const memberRef = doc(db, TEAMS_COLLECTION, memberId);
        await updateDoc(memberRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        });
        // console.log('✅ Team member updated:', memberId);
        return { id: memberId, ...updates };
    } catch (error) {
        console.error('❌ Error updating team member:', error);
        throw new Error(`Failed to update team member: ${error.message}`);
    }
};

export default {
    createStartup,
    getStartups,
    getStartupById,
    updateStartup,
    deleteStartup,
    searchStartups,
    addTeamMember,
    getTeamMembers,
    deleteTeamMember,
    updateTeamMember,
};
